// Persistent nudge banner encouraging users to grant notification permission.
//
// Shows at the top of the app when permission is in the "default" state
// (never decided). Hides permanently when granted (no need) or denied
// (browser won't let us re-ask anyway).
//
// Once dismissed, stays hidden for 24h via localStorage.

import React, { useState, useEffect } from 'react';
import { Bell, X } from 'lucide-react';
import { useNotifications } from './NotificationHandler';

const DISMISS_KEY = 'notifPermBannerDismissedAt';
const RENUDGE_MS = 24 * 60 * 60 * 1000; // 24h

export const NotificationPermissionBanner: React.FC = () => {
  const { notificationPermission, requestPermission } = useNotifications();
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    try {
      const lastDismissed = Number(localStorage.getItem(DISMISS_KEY) || 0);
      if (lastDismissed && Date.now() - lastDismissed < RENUDGE_MS) {
        setDismissed(true);
      }
    } catch {
      /* ignore */
    }
  }, []);

  if (notificationPermission !== 'default') return null;
  if (dismissed) return null;

  const handleEnable = () => {
    requestPermission();
  };

  const handleDismiss = () => {
    setDismissed(true);
    try {
      localStorage.setItem(DISMISS_KEY, String(Date.now()));
    } catch {
      /* ignore */
    }
  };

  return (
    <div className="bg-brand-50 border-b border-brand-200 px-4 py-2.5 flex items-center gap-3 shrink-0">
      <Bell size={16} className="text-brand-600 shrink-0" />
      <p className="text-xs text-brand-900 flex-1">
        <span className="font-semibold">Enable notifications</span> to stay updated
        on new orders, messages, and stock updates — even when this tab isn't focused.
      </p>
      <button
        onClick={handleEnable}
        className="bg-brand-600 hover:bg-brand-700 text-white text-xs font-semibold px-3 py-1.5 rounded-md transition-colors"
      >
        Enable
      </button>
      <button
        onClick={handleDismiss}
        className="p-1 text-brand-400 hover:text-brand-600 transition-colors"
        aria-label="Dismiss"
      >
        <X size={14} />
      </button>
    </div>
  );
};
